import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { juntar } from './ui'

/**
 * Botão só com ícone, e o nome aparecendo no hover.
 *
 * O `title` do navegador resolveria, mas leva quase um segundo para aparecer
 * e some sozinho — ruim para uma barra em que a pessoa está procurando o que
 * cada desenho faz. Aqui a dica é instantânea.
 *
 * O nome vai também em `aria-label`: leitor de tela não passa o mouse, e um
 * botão sem texto acessível é um botão sem nome.
 */
export function BotaoIcone({
  rotulo,
  children,
  destaque = false,
  perigo = false,
  vermelho = false,
  pequeno = false,
  dicaAlinhada = 'centro',
  className,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  /** O que o botão faz. Vira a dica e o nome acessível. */
  rotulo: string
  children: ReactNode
  destaque?: boolean
  perigo?: boolean
  /**
   * Vermelho já em repouso, não só no hover.
   *
   * É prop e não `className` porque `text-red-600` passado por fora perdia
   * para o `text-tinta-600` da base: as duas têm a mesma especificidade, e
   * quem decide é a ordem na folha de estilo gerada, não a ordem no atributo.
   * Escolhendo aqui, só uma das duas chega ao HTML.
   */
  vermelho?: boolean
  /**
   * Botão de 24 px, para caber em cartão apertado.
   *
   * É prop pelo mesmo motivo de `vermelho`: `h-6 w-6` passado por `className`
   * perde para o `h-8 w-8` da base — as duas têm a mesma especificidade, e
   * quem decide é a ordem na folha de estilo gerada.
   */
  pequeno?: boolean
  /**
   * Para onde a dica cresce.
   *
   * Centrada é o certo no meio de uma barra. No último botão de uma barra
   * colada na borda direita, a metade de fora da dica saía da tela — o texto
   * ficava cortado justamente onde ninguém consegue rolar para ver.
   */
  dicaAlinhada?: 'centro' | 'direita'
}) {
  const caixa = useRef<HTMLSpanElement>(null)
  const [dica, setDica] = useState(false)

  return (
    <span
      ref={caixa}
      className="relative inline-flex"
      onMouseEnter={() => setDica(true)}
      onMouseLeave={() => setDica(false)}
    >
      <button
        type="button"
        aria-label={rotulo}
        className={juntar(
          'relative inline-flex items-center justify-center rounded-md transition',
          pequeno ? 'h-6 w-6' : 'h-8 w-8',
          'disabled:cursor-not-allowed disabled:opacity-40',
          destaque
            ? 'bg-marca text-white hover:bg-marca-escura'
            : vermelho
              ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
              : perigo
                ? 'text-tinta-400 hover:bg-red-50 hover:text-red-600'
                : 'text-tinta-600 hover:bg-tinta-100 hover:text-tinta-900',
          className,
        )}
        {...resto}
      >
        {children}
      </button>

      {dica ? (
        <Dica ancora={caixa} alinhada={dicaAlinhada} aoSumir={() => setDica(false)}>
          {rotulo}
        </Dica>
      ) : null}
    </span>
  )
}

const FOLGA = 4

/**
 * A dica em si, solta no `body`.
 *
 * Antes ela ficava dentro do botão, sempre embaixo dele. Na barra de escrita,
 * que mora no pé da tela, abria abaixo da janela: "Anexar", "Materiais" e
 * "Emoji" não mostravam o nome para ninguém. E dentro de caixa que corta o
 * que passa da borda (a coluna da conversa), sumia pela metade.
 *
 * Agora abre embaixo quando cabe e em cima quando não, sem passar das bordas
 * da janela. `pointer-events-none` para a dica não roubar o hover do próprio
 * botão quando aparece por cima do que está ao lado.
 */
function Dica({
  ancora,
  alinhada,
  aoSumir,
  children,
}: {
  ancora: RefObject<HTMLSpanElement | null>
  alinhada: 'centro' | 'direita'
  aoSumir: () => void
  children: ReactNode
}) {
  const el = useRef<HTMLSpanElement>(null)
  const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const a = ancora.current?.getBoundingClientRect()
    const d = el.current?.getBoundingClientRect()
    if (!a || !d) return
    const cabeEmbaixo = a.bottom + FOLGA + d.height <= window.innerHeight - FOLGA
    const top = cabeEmbaixo ? a.bottom + FOLGA : a.top - FOLGA - d.height
    const desejada = alinhada === 'direita' ? a.right - d.width : a.left + (a.width - d.width) / 2
    const left = Math.min(Math.max(FOLGA, desejada), window.innerWidth - d.width - FOLGA)
    setPosicao({ top, left })
  }, [ancora, alinhada])

  // A dica fica presa à posição de quando apareceu: rolou, some.
  useEffect(() => {
    window.addEventListener('scroll', aoSumir, true)
    return () => window.removeEventListener('scroll', aoSumir, true)
  }, [aoSumir])

  return createPortal(
    <span
      ref={el}
      role="tooltip"
      style={{
        position: 'fixed',
        top: posicao?.top ?? 0,
        left: posicao?.left ?? 0,
        visibility: posicao ? 'visible' : 'hidden',
        zIndex: 80,
      }}
      className="pointer-events-none whitespace-nowrap rounded bg-tinta-900 px-2 py-1 text-xs text-tinta-50"
    >
      {children}
    </span>,
    document.body,
  )
}
